import { capitalize } from "@/helpers/capitalize";
import { categoryLabelKeys } from "@/helpers/articleMainCategory";
import { canonicalCategoryKey } from "@/helpers/categoryKey";
import { useTranslations } from "next-intl";

/**
 * Les catégories d'un article ou d'une formation, en une seule ligne de texte.
 *
 * ## Un seul jeu de libellés
 *
 * Le libellé vient de `courses_category_*`, celui des FILTRES. Deux jeux
 * coexistaient et se contredisaient sur la même page : le fil d'Ariane disait
 * « Développeurs » et ce bloc « Développement », pour la même catégorie. Le
 * lecteur qui clique doit retrouver le mot du bouton où il atterrit.
 *
 * `translateCategories_*` reste le repli, et il est nécessaire : seules les
 * trois catégories filtrables ont un libellé de filtre, alors que ce bloc
 * affiche tout ce que porte l'article, « designers » ou « startup » compris.
 * Faute des deux, le nom de la base est capitalisé, ce qui reste lisible pour
 * une catégorie neuve que personne n'a encore traduite.
 *
 * ## L'ordre
 *
 * `mainCategory` passe en tête quand elle est fournie, sans rien retirer de la
 * liste. L'ordre venait sinon de la base, c'est-à-dire d'une ManyToMany sans
 * `OrderBy` : « Développement, Entrepreneuriat, Startup, No-Code » sur un
 * article dont le fil d'Ariane annonçait « Entrepreneurs ». Les formations
 * n'ont pas de principale et gardent donc l'ordre qu'elles avaient.
 */
export default function TranslateCategories({ categories, mainCategory }) {
    const t = useTranslations();

    const principale = canonicalCategoryKey(mainCategory);
    const rangees = [...(categories || [])];

    if (principale) {
        rangees.sort((a, b) => {
            const aPrincipale = canonicalCategoryKey(a?.name) === principale;
            const bPrincipale = canonicalCategoryKey(b?.name) === principale;

            return Number(bPrincipale) - Number(aPrincipale);
        });
    }

    const libelle = (name) => {
        const { filtre, sujet } = categoryLabelKeys(name);

        for (const cle of [filtre, sujet]) {
            if (t.has(cle)) return t(cle);
        }

        return capitalize(name);
    };

    return rangees
        .filter((category) => typeof category?.name === "string")
        .map((category) => libelle(category.name))
        .join(", ");
}
